import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { GET_TESTS } from "@/graphql/queries";
import { mapToSnakeCase } from "@/graphql/mappers";
import { useFilters } from "@/context/FilterContext";
import { useApp } from "@/context/AppContext";
import { DataTable } from "@/components/DataTable";
import { Pagination } from "@/components/Pagination";
import type { Test } from "@/types";

export function TestsList() {
  const { filters, setFilter, clearFilters, page, pageSize } = useFilters();
  const { navigateToCreateTest, activeUser } = useApp();

  const { data: testsRaw, loading: isLoading } = useQuery<any>(GET_TESTS);
  const tests: Test[] = testsRaw ? mapToSnakeCase(testsRaw.tests) : [];

  // Collect unique tags for the tag filter dropdown
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    for (const t of tests) {
      for (const tag of t.tags || []) tagSet.add(tag);
    }
    return Array.from(tagSet).sort();
  }, [tests]);

  // Collect unique suites for the suite filter dropdown
  const allSuites = useMemo(() => {
    const suiteSet = new Set<string>();
    for (const t of tests) {
      if (t.suite) suiteSet.add(t.suite);
    }
    return Array.from(suiteSet).sort();
  }, [tests]);

  // Collect unique statuses for the status filter dropdown
  const allStatuses = useMemo(() => {
    const statusSet = new Set<string>();
    for (const t of tests) {
      if (t.jira_status) statusSet.add(t.jira_status);
    }
    return Array.from(statusSet).sort();
  }, [tests]);

  // Compute status counts for the score section
  // Normalize raw jira_status values into display categories
  const scoreCategories = ["complete", "pending", "missing link", "unknown"] as const;
  const statusToCategory: Record<string, string> = {
    "done": "complete", "Done": "complete",
    "pending": "pending", "In Progress": "pending", "not_done": "pending",
    "missing link": "missing link",
    "unknown": "unknown",
  };
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { complete: 0, pending: 0, "missing link": 0, unknown: 0 };
    for (const t of tests) {
      const raw = t.jira_status || "unknown";
      const cat = statusToCategory[raw] || "unknown";
      counts[cat] = (counts[cat] || 0) + 1;
    }
    return counts;
  }, [tests]);

  const scoreCategoryColor: Record<string, string> = {
    "complete": "green",
    "pending": "yellow",
    "missing link": "red",
    "unknown": "text2",
  };

  const textColorClass: Record<string, string> = {
    "green": "text-green", "yellow": "text-yellow", "red": "text-red",
    "text2": "text-text2",
  };
  const dotColorClass: Record<string, string> = {
    "green": "before:bg-green", "yellow": "before:bg-yellow", "red": "before:bg-red",
    "text2": "before:bg-text2",
  };

  const hasAnyFilter = filters.search || filters.testType || filters.testStatus || filters.testHasIssues || filters.testTag || filters.testSuite;

  const filtered = tests.filter((t: Test) => {
    const searchText = `${t.id} ${t.title || ""} ${t.description || ""} ${(t.jira_tickets || []).join(" ")}`;
    if (filters.search && !searchText.toLowerCase().includes(filters.search.toLowerCase())) return false;
    if (filters.testType && t.type !== filters.testType) return false;
    if (filters.testSuite && t.suite !== filters.testSuite) return false;
    if (filters.testStatus && t.jira_status !== filters.testStatus) return false;
    if (filters.testHasIssues === "has_issues" && !t.has_issues) return false;
    if (filters.testHasIssues === "no_issues" && t.has_issues) return false;
    if (filters.testHasIssues === "no_source_link") {
      const issues = t.issues || [];
      if (!issues.includes("no source link")) return false;
    }
    if (filters.testHasIssues === "no_jira") {
      const issues = t.issues || [];
      if (!issues.includes("jira-tracked test has no Jira ticket") && !issues.includes("manual test has no Jira ticket")) return false;
    }
    if (filters.testHasIssues === "has_jira") {
      const tickets = t.jira_tickets || [];
      if (tickets.length === 0) return false;
    }
    if (filters.testTag) {
      const tags = t.tags || [];
      if (filters.testTag === "__not_tagged__") {
        if (tags.length > 0) return false;
      } else {
        if (!tags.includes(filters.testTag)) return false;
      }
    }
    return true;
  });

  const paged = filtered.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Page header */}
      <div className="px-7 pt-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-text leading-tight">Tests</h1>
          </div>
          {activeUser && activeUser.role !== "viewer" && (
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-default border text-xs font-medium cursor-pointer whitespace-nowrap transition-all duration-150 ease-in-out bg-accent text-white border-accent hover:bg-accent2 hover:border-accent2"
              onClick={() => navigateToCreateTest()}
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-3.5 h-3.5">
                <path d="M8 3v10M3 8h10" />
              </svg>
              New
            </button>
          )}
        </div>
        {/* Summary stats */}
        <div className="flex flex-wrap items-stretch mt-3" data-testid="test-stats-bar">
          <div className="flex flex-col items-start px-4.5 py-2.5 border-r border-border first:pl-0 last:border-r-0">
            <div className="text-lg font-bold text-blue leading-tight">{tests.length}</div>
            <div className="text-[10px] text-text2 uppercase tracking-wide mt-px flex items-center gap-1 before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full before:bg-blue before:shrink-0">Total</div>
          </div>
          {scoreCategories.map(cat => {
            const color = scoreCategoryColor[cat] || "text2";
            return (
              <div key={cat} className="flex flex-col items-start px-4.5 py-2.5 border-r border-border first:pl-0 last:border-r-0">
                <div className={`text-lg font-bold leading-tight ${textColorClass[color] || "text-text2"}`}>{statusCounts[cat] ?? 0}</div>
                <div className={`text-[10px] text-text2 uppercase tracking-wide mt-px flex items-center gap-1 before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full before:shrink-0 ${dotColorClass[color] || "before:bg-text2"}`}>{cat}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Search + clear */}
      <div className="flex items-center justify-between px-7 pt-3.5 pb-2.5 gap-2">
        <div className="relative flex-1 max-w-80 min-w-40">
          <svg className="absolute left-[10px] top-1/2 -translate-y-1/2 text-text2 pointer-events-none w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="6.5" cy="6.5" r="4.5" /><path d="M10 10l4 4" />
          </svg>
          <input
            type="text"
            value={filters.search}
            onChange={e => setFilter("search", e.target.value)}
            placeholder="Search tests..."
            className="w-full py-[5px] pr-[10px] pl-[30px] bg-bg border border-border rounded-default text-text text-xs outline-none transition-colors duration-[120ms] focus:border-accent placeholder:text-text2"
          />
        </div>
        {hasAnyFilter && (
          <button onClick={clearFilters} className="px-2.5 py-1 bg-transparent border border-border rounded-default text-text2 text-[11px] cursor-pointer transition-colors duration-[120ms] hover:text-accent hover:border-accent whitespace-nowrap">Clear filters</button>
        )}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 px-7 pb-2.5 items-center">
        <select value={filters.testType} onChange={e => setFilter("testType", e.target.value)} aria-label="Type" className="px-2 py-1 bg-bg border border-border rounded-default text-text text-[11px] outline-none transition-colors duration-[120ms] min-w-[100px] h-7 focus:border-accent">
          <option value="">All types</option>
          {["checklist", "jira-tracked", "pipeline"].map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        <select value={filters.testSuite} onChange={e => setFilter("testSuite", e.target.value)} aria-label="Suite" className="px-2 py-1 bg-bg border border-border rounded-default text-text text-[11px] outline-none transition-colors duration-[120ms] min-w-[100px] h-7 focus:border-accent">
          <option value="">All suites</option>
          {allSuites.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <select value={filters.testStatus} onChange={e => setFilter("testStatus", e.target.value)} aria-label="Status" className="px-2 py-1 bg-bg border border-border rounded-default text-text text-[11px] outline-none transition-colors duration-[120ms] min-w-[100px] h-7 focus:border-accent">
          <option value="">All statuses</option>
          {allStatuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <select value={filters.testHasIssues} onChange={e => setFilter("testHasIssues", e.target.value)} aria-label="Issues" className="px-2 py-1 bg-bg border border-border rounded-default text-text text-[11px] outline-none transition-colors duration-[120ms] min-w-[100px] h-7 focus:border-accent">
          <option value="">All issues</option>
          <option value="has_issues">Has issues</option>
          <option value="no_issues">No issues</option>
          <option value="no_source_link">No source link</option>
          <option value="no_jira">No jira</option>
          <option value="has_jira">Has jira</option>
        </select>

        <select value={filters.testTag} onChange={e => setFilter("testTag", e.target.value)} aria-label="Tag" className="px-2 py-1 bg-bg border border-border rounded-default text-text text-[11px] outline-none transition-colors duration-[120ms] min-w-[100px] h-7 focus:border-accent">
          <option value="">All tags</option>
          <option value="__not_tagged__">Not tagged</option>
          {allTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto min-h-0">
        {isLoading ? (
          <div className="text-center p-8 text-text2">Loading tests...</div>
        ) : (
          <DataTable items={paged} view="tests" />
        )}
      </div>

      <Pagination totalItems={filtered.length} />
    </div>
  );
}
